import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
    ComposedChart,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Bar,
    Customized,
    Label,
    Legend
} from "recharts";

const stageGroups = [
    { name: "Stages", color: "rgba(250, 195, 196, 0.25)", range: [0, 8] },
];

const statusColors = {
    "completed": "#4CAF50",
    "in-process": "#FFC107",
    "stopped": "#F44336",
    "NA": "#9E9E9E"
};

const SalesAIChart = ({ data, stages }) => {
    const rowHeight = 40;
    const whiteBox = 170;

    const getTransformedData = () => {
        const transformedData = [];
        data.forEach((group) => {
            group.sub_tasks.forEach((sub, index) => {
                const row = { task: sub.task, mainTask: index === 0 ? group.task : "" };
                stages.forEach((stage, i) => {
                    if (sub[stage]) {
                        row[`stage_${i}`] = 1;
                        row[`meta_${i}`] = { ...sub[stage], stage };
                    } else {
                        row[`stage_${i}`] = 0;
                    }
                });
                transformedData.push(row);
            });
        });
        return transformedData;
    }

    // Custom Y-Axis Tick with main task label
    const CustomYAxisTick = ({ x, y, payload, index }) => {
        const row = getTransformedData()[index];

        const tooltipMainTask = (name) => (
            <Tooltip className='agent-tooltip'>
                {name}
            </Tooltip>
        );
        return (
            <g transform={`translate(${x},${y})`}>
                {row.mainTask && (
                    <OverlayTrigger placement="top" overlay={tooltipMainTask(row.mainTask)}>
                        <text
                            x={-220}
                            y={-15}
                            fontSize={14}
                            fill="gray"
                            textAnchor="start"
                        >
                            {row.mainTask.length > 20 ? `${row.mainTask.slice(0, 20)}...` : row.mainTask}
                        </text>
                    </OverlayTrigger>
                )}
                <OverlayTrigger placement="top" overlay={tooltipMainTask(payload.value)}>
                    <text x={0} y={0} dy={5} fontSize={12} textAnchor="end">
                        {payload.value.length > 20 ? `${payload.value.slice(0, 20)}...` : payload.value}
                    </text>
                </OverlayTrigger>
            </g>
        );
    };

    const WrappedXAxisTick = ({ x, y, payload, index }) => {
        const stageIndex = Math.floor(payload.value); // get index
        const label = stages[stageIndex] || "";

        const words = label.replaceAll("_", " ").split(" ");
        const mid = Math.ceil(words.length / 2);
        const line1 = words.slice(0, mid).join(" ");
        const line2 = words.slice(mid).join(" ");

        return (
            <g transform={`translate(${x},${y})`}>
                <text textAnchor="middle" fill="#666" fontSize={10}>
                    <tspan x={0} dy={-12}>{line1.charAt(0).toUpperCase() + line1.slice(1).toLowerCase()}</tspan>
                    <tspan x={0} dy={12}>{line2.charAt(0).toUpperCase() + line2.slice(1).toLowerCase()}</tspan>
                </text>
            </g>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={getTransformedData().length * (rowHeight + 40) + 150}>
            <ComposedChart
                layout="vertical"
                data={getTransformedData()}
                margin={{ top: 40, right: 50, left: 50, bottom: 20 }}
            >
                {/* Grouped background blocks */}
                <Customized
                    component={({ height }) => {
                        let y = 0;
                        return getTransformedData().map((row, index) => {
                            if (row.mainTask) {
                                const group = data.find(g => g.task === row.mainTask);
                                const blockHeight = group.sub_tasks.length * rowHeight;
                                const fill = index % 2 === 0 ? "#f0f0ff" : "#f9f9f9";
                                const el = (
                                    <rect
                                        key={index}
                                        x={0}
                                        y={y}
                                        width="100%"
                                        height={blockHeight}
                                        fill='transparent'
                                        opacity={0.4}
                                    />
                                );
                                y += blockHeight;
                                return el;
                            }
                            return null;
                        });
                    }}
                />

                {/* Stage group background headers */}
                <Customized
                    component={({ xAxisMap, height }) => {
                        const xAxis = xAxisMap[0];
                        return stageGroups.map((group, idx) => {
                            const xStart = xAxis.scale(group.range[0]);
                            const xEnd = xAxis.scale(group.range[1]);
                            const rectX = xStart;
                            const rectWidth = xEnd - xStart;
                            const labelX = xStart + rectWidth / 2;
                            return (
                                <g key={idx}>
                                    <rect
                                        x={rectX}
                                        y={0}
                                        width={rectWidth}
                                        height={height}
                                        fill={group.color}
                                        opacity={0.3}
                                    />
                                    <text
                                        x={labelX}
                                        y={20}
                                        textAnchor="middle"
                                        fontSize={12}
                                        fill="#333"
                                        fontWeight="600"
                                    >
                                        {group.name}
                                    </text>
                                </g>
                            );
                        });
                    }}
                />

                <XAxis
                    type="number"
                    orientation="top"
                    domain={[0, stages.length]}
                    ticks={stages.map((_, i) => i + 0.5)}
                    tick={<WrappedXAxisTick stages={stages} />}
                    interval={0}
                />
                <YAxis
                    type="category"
                    dataKey="task"
                    tick={<CustomYAxisTick />}
                    width={250}
                >
                    <Label value="Initiates"
                        angle={-90}
                        position={{ x: 0, y: '50%' }}
                        textAnchor="start"
                        style={{ textAnchor: 'middle', fill: '#333', fontSize: 16, fontWeight: 'bold' }} />
                </YAxis>
                <Legend verticalAlign="bottom" content={StatusLegend} />
                {/* Bars */}
                {stages.map((stage, i) => (
                    <Bar
                        key={stage}
                        dataKey={`stage_${i}`}
                        stackId="a"
                        barSize={30}
                        shape={(props) => {
                            const { x, y, height, payload } = props;
                            const meta = payload[`meta_${i}`];
                            if (!meta || !meta.status) return null;
                            const tooltipResult = (
                                <Tooltip className='agent-tooltip'>
                                    {meta.assignee}
                                </Tooltip>
                            );
                            return (
                                <g>
                                    <rect
                                        x={x}
                                        y={y}
                                        width={whiteBox}
                                        height={height}
                                        fill={statusColors[meta.status]}
                                    />
                                    {/* <rect
                                        x={x + whiteBox}
                                        y={y}
                                        width={20}
                                        height={height}
                                        fill={statusColors[meta.status] || "#ccc"}
                                    /> */}
                                    {meta.assignee && (
                                        <OverlayTrigger placement="right" overlay={tooltipResult}>
                                            <g>
                                                <rect
                                                    x={x + whiteBox + 10}
                                                    y={y}
                                                    rx={15}
                                                    width={30}
                                                    height={height}
                                                    fill='gray'
                                                />

                                                <text
                                                    x={x + whiteBox + 25}
                                                    y={y + height / 2 + 5}
                                                    textAnchor="middle"
                                                    fontSize={16}
                                                    fill="white"
                                                >
                                                    {meta.assignee[0]}
                                                </text>
                                            </g>
                                        </OverlayTrigger>
                                    )}
                                </g>
                            );
                        }}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default SalesAIChart;


export const StatusLegend = () => {
    const items = [
        { label: "Completed", color: statusColors.completed },
        { label: "In Progress", color: statusColors["in-process"] },
        { label: "Blocked", color: statusColors.stopped },
        { label: "Not Started", color: statusColors.NA },
    ];

    return (
        <div style={{ display: "flex", justifyContent: 'center', gap: "1rem", paddingTop: '10px' }}>
            {items.map((item) => (
                <div key={item.label} style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            backgroundColor: item.color,
                            width: 12,
                            height: 12,
                            display: "inline-block",
                            marginRight: 6,
                        }}
                    />
                    <span>{item.label}</span>
                </div>
            ))}
        </div>
    );
};